import React from 'react'
import JSZip from 'jszip'
import { generateQRCodeBlob } from '../../../utils/qrGenerator'
import { sanitizeFileSystemName } from '../../../utils/bookletFolderUtils'

interface GenerateQRCodeParams {
  qrCodeRef: React.RefObject<HTMLDivElement>
  associationName: string
}

/**
 * Generates QR code as both PNG and PDF
 */
export const generateQRCode = async ({
  qrCodeRef,
  associationName
}: GenerateQRCodeParams): Promise<JSZip> => {
  const zip = new JSZip()

  if (!qrCodeRef.current) {
    return zip
  }

  try {
    const svg = qrCodeRef.current.querySelector('svg')
    if (!svg) {
      return zip
    }

    // Generate PNG
    const qrPngBlob = await generateQRCodeBlob(svg)
    const sanitizedAssociation = sanitizeFileSystemName(associationName)
    zip.file(`${sanitizedAssociation}-QR-kod.png`, qrPngBlob)

    // Generate PDF from PNG using jsPDF
    // Import jsPDF dynamically
    const { default: jsPDF } = await import('jspdf')

    // Convert blob to image data URL
    const imageUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(qrPngBlob)
    })

    // Create PDF with QR code image centered on A4 page
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    })

    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const imgWidth = 80 // 80mm width
    const imgHeight = 80 // 80mm height (square)
    const x = (pageWidth - imgWidth) / 2
    const y = (pageHeight - imgHeight) / 2

    pdf.addImage(imageUrl, 'PNG', x, y, imgWidth, imgHeight)
    const pdfBlob = pdf.output('blob')

    zip.file(`${sanitizedAssociation}-QR-kod.pdf`, pdfBlob)
  } catch (error) {
    console.error('Error generating QR code:', error)
  }

  return zip
}

